/**
 * Browser environment implementation.
 * Uses File API, Web Crypto, SparkMD5, and DOM elements for media info.
 */
import SparkMD5 from 'spark-md5'
import type { FileInfo, MediaDimensions, HashAlgorithm } from './types'
import {
  formatSize,
  formatTime,
  getExtension,
  getStem,
  bufferToHex,
  inferMimeType,
  DEFAULT_CHUNK_SIZE,
} from './core'

/**
 * Read a file in chunks and feed them to SparkMD5.
 * This avoids loading the entire file into memory.
 */
function computeMd5Chunked(file: File, chunkSize = DEFAULT_CHUNK_SIZE): Promise<string> {
  return new Promise((resolve, reject) => {
    const spark = new SparkMD5.ArrayBuffer()
    const fileReader = new FileReader()
    let offset = 0

    fileReader.onload = (e) => {
      if (!e.target?.result) {
        reject(new Error('FileReader returned no data'))
        return
      }
      spark.append(e.target.result as ArrayBuffer)
      offset += chunkSize
      if (offset < file.size) {
        readNext()
      } else {
        resolve(spark.end())
      }
    }

    fileReader.onerror = () => {
      reject(new Error(`Failed to read file for MD5: ${fileReader.error?.message || 'unknown error'}`))
    }

    function readNext() {
      const slice = file.slice(offset, offset + chunkSize)
      fileReader.readAsArrayBuffer(slice)
    }

    readNext()
  })
}

/**
 * Compute SHA-256 hash using Web Crypto API.
 * Reads the entire file (Web Crypto has no streaming digest).
 * For very large files (>500MB), consider the trade-off.
 */
async function computeSha256Browser(file: File): Promise<string> {
  if (typeof crypto === 'undefined' || !crypto.subtle) {
    throw new Error('Web Crypto API is not available in this browser')
  }
  const buffer = await file.arrayBuffer()
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer)
  return bufferToHex(hashBuffer)
}

/**
 * Get image dimensions from a File object.
 * Creates a temporary Image element and cleans it up.
 */
function getImgDimensions(file: File): Promise<MediaDimensions> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.style.display = 'none'

    const cleanup = () => {
      img.onload = null
      img.onerror = null
      URL.revokeObjectURL(url)
      if (img.parentNode) img.parentNode.removeChild(img)
    }

    img.onload = () => {
      const result = { width: img.naturalWidth, height: img.naturalHeight }
      cleanup()
      resolve(result)
    }

    img.onerror = () => {
      cleanup()
      reject(new Error(`Failed to load image: ${file.name}`))
    }

    img.src = url
    // Append to DOM to ensure onload fires in all browsers
    document.body.appendChild(img)
  })
}

/**
 * Get video dimensions and duration from a File object.
 */
function getVideoDimensions(file: File): Promise<MediaDimensions> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const video = document.createElement('video')
    video.style.display = 'none'
    video.preload = 'metadata'

    const cleanup = () => {
      video.onloadedmetadata = null
      video.onerror = null
      URL.revokeObjectURL(url)
      if (video.parentNode) video.parentNode.removeChild(video)
      // Release media resources
      video.src = ''
      video.load()
    }

    video.onloadedmetadata = () => {
      const result: MediaDimensions = {
        width: video.videoWidth,
        height: video.videoHeight,
        duration: Math.round(video.duration),
      }
      cleanup()
      resolve(result)
    }

    video.onerror = () => {
      cleanup()
      reject(new Error(`Failed to load video: ${file.name}`))
    }

    video.src = url
    document.body.appendChild(video)
  })
}

/**
 * Get media dimensions for image or video files.
 * Returns empty object for non-media files.
 */
export async function getMediaDimensions(file: File): Promise<Partial<MediaDimensions>> {
  const mimeType = file.type || inferMimeType(file.name)
  const category = mimeType.split('/')[0]

  try {
    if (category === 'image') {
      return await getImgDimensions(file)
    }
    if (category === 'video') {
      return await getVideoDimensions(file)
    }
  } catch {
    // Silently ignore dimension errors for non-critical info
  }
  return {}
}

/**
 * Compute file hash (MD5 or SHA-256) in browser.
 */
export async function computeHash(file: File, algorithm: HashAlgorithm): Promise<string> {
  if (algorithm === 'md5') return computeMd5Chunked(file)
  if (algorithm === 'sha256') return computeSha256Browser(file)
  throw new Error(`Unsupported hash algorithm: ${algorithm}`)
}

/**
 * Browser implementation: extract file info from a File/Blob object.
 */
export async function getFileInfoFromBrowser(file: File): Promise<FileInfo> {
  const mimeType = file.type || inferMimeType(file.name)
  const extension = getExtension(file.name)
  const size = file.size

  // Run independent operations in parallel
  const [md5, sha256, dimensions] = await Promise.all([
    computeHash(file, 'md5').catch(() => ''),
    computeHash(file, 'sha256').catch(() => ''),
    getMediaDimensions(file),
  ])

  return {
    name: file.name,
    stem: getStem(file.name),
    extension,
    size,
    sizeFormatted: formatSize(size),
    mimeType,
    md5,
    sha256,
    lastModified: file.lastModified,
    lastModifiedFormatted: formatTime(file.lastModified),
    ...dimensions,
  }
}
